import { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Send, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { getAPIBase } from '../lib/api-fetch';

interface ContactAgentFormProps {
  propertyTitle: string;
  propertyPrice: string;
  agentName: string;
  agentEmail: string;
  propertyId?: string;
  agentId?: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  phone?: string;
  message?: string;
}

export function ContactAgentForm({ propertyTitle, propertyPrice, agentName, agentEmail, propertyId, agentId }: ContactAgentFormProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: `Hi ${agentName}, I'm interested in "${propertyTitle}" listed at ${propertyPrice}. Please contact me with more details.`,
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const validate = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name || formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }

    if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.phone || !/^[\+]?[\d\s\-()]{7,}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    if (!formData.message || formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);

    try {
      const [firstName, ...rest] = formData.name.trim().split(' ');
      const lastName = rest.join(' ') || firstName;

      const res = await fetch(`${getAPIBase()}/public/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName,
          lastName,
          email: formData.email,
          phone: formData.phone,
          message: formData.message,
          propertyId,
          agentId,
        }),
      });

      if (!res.ok) throw new Error(`Server error: ${res.status}`);

      setIsSubmitting(false);
      setIsSubmitted(true);
      toast.success('Message sent to agent!', {
        description: `${agentName} will respond to you soon.`,
      });
    } catch {
      setIsSubmitting(false);
      toast.error('Failed to send message. Please try again.');
    }
  };

  if (isSubmitted) {
    return (
      <Card className="border-emerald-500/30 bg-emerald-500/5">
        <CardContent className="pt-6">
          <div className="text-center space-y-3">
            <div className="w-12 h-12 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="w-6 h-6 text-emerald-500" />
            </div>
            <h3 className="font-semibold text-lg">Message Sent!</h3>
            <p className="text-sm text-muted-foreground">
              {agentName} will get back to you shortly.
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setIsSubmitted(false);
                setFormData(prev => ({ ...prev, name: '', email: '', phone: '' }));
              }}
              data-testid="contact-agent-send-another-button"
            >
              Send Another Message
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Send className="w-4 h-4 text-primary" />
          Send a Message
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="contact-name" className="text-xs">Name *</Label>
            <Input
              id="contact-name"
              value={formData.name}
              onChange={e => { setFormData(prev => ({ ...prev, name: e.target.value })); setErrors(prev => ({ ...prev, name: undefined })); }}
              placeholder="Your full name"
              className={errors.name ? 'border-red-500' : ''}
            />
            {errors.name && (
              <p className="text-xs text-red-500 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />{errors.name}
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="contact-email" className="text-xs">Email *</Label>
            <Input
              id="contact-email"
              type="email"
              value={formData.email}
              onChange={e => { setFormData(prev => ({ ...prev, email: e.target.value })); setErrors(prev => ({ ...prev, email: undefined })); }}
              placeholder="you@example.com"
              className={errors.email ? 'border-red-500' : ''}
            />
            {errors.email && (
              <p className="text-xs text-red-500 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />{errors.email}
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="contact-phone" className="text-xs">Phone *</Label>
            <Input
              id="contact-phone"
              type="tel"
              value={formData.phone}
              onChange={e => { setFormData(prev => ({ ...prev, phone: e.target.value })); setErrors(prev => ({ ...prev, phone: undefined })); }}
              placeholder="+27 72 123 4567"
              className={errors.phone ? 'border-red-500' : ''}
            />
            {errors.phone && (
              <p className="text-xs text-red-500 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />{errors.phone}
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="contact-message" className="text-xs">Message *</Label>
            <Textarea
              id="contact-message"
              value={formData.message}
              onChange={e => { setFormData(prev => ({ ...prev, message: e.target.value })); setErrors(prev => ({ ...prev, message: undefined })); }}
              rows={3}
              className={errors.message ? 'border-red-500' : ''}
            />
            {errors.message && (
              <p className="text-xs text-red-500 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />{errors.message}
              </p>
            )}
          </div>

          <Button type="submit" className="w-full bg-[#10B981] hover:bg-[#10B981]/90" disabled={isSubmitting} data-testid="contact-agent-submit-button">
            {isSubmitting ? 'Sending...' : 'Contact Agent'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
